import React, { useRef, useState } from 'react'
import { motion, useInView } from 'framer-motion'
import axios from 'axios'
import { CONTACTO } from '../constants/testIds'

const API = `${process.env.REACT_APP_BACKEND_URL}/api`
const BF = 'Anton, sans-serif'
const MONO = 'JetBrains Mono, monospace'
const SG = 'Space Grotesk, sans-serif'

function RevealLine({ children, delay = 0 }) {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-60px' })
  return (
    <div ref={ref} style={{ overflow: 'hidden', paddingTop: '0.1em', marginTop: '-0.1em', paddingBottom: '0.06em', marginBottom: '-0.06em' }}>
      <motion.div initial={{ y: '105%' }} animate={inView ? { y: 0 } : {}}
        transition={{ delay, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}>
        {children}
      </motion.div>
    </div>
  )
}

function FadeIn({ children, delay = 0, className = '' }) {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-60px' })
  return (
    <motion.div ref={ref} className={className}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
      {children}
    </motion.div>
  )
}

export default function Contacto() {
  const [form, setForm] = useState({ name: '', email: '', message: '' })
  const [status, setStatus] = useState('idle')
  const [errorMsg, setErrorMsg] = useState('')

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value })

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return
    setStatus('loading')
    setErrorMsg('')
    try {
      await axios.post(`${API}/contact`, form)
      setStatus('success')
      setForm({ name: '', email: '', message: '' })
    } catch (err) {
      setStatus('error')
      setErrorMsg(err?.response?.data?.detail || 'Error al enviar. Inténtalo de nuevo.')
      setTimeout(() => setStatus('idle'), 5000)
    }
  }

  return (
    <section id="contacto" data-testid={CONTACTO.section} className="bg-black py-24 md:py-36 border-t border-neutral-900">
      <div className="max-w-screen-xl mx-auto px-6 md:px-12 xl:px-16">

        <FadeIn delay={0}>
          <div className="flex items-center gap-4 mb-14">
            <span className="accent-rule" />
            <span className="text-xs tracking-[0.35em] text-amber-400 uppercase" style={{ fontFamily: MONO }}>Contacto</span>
          </div>
        </FadeIn>

        {/* Centered heading — Anton, full-width, no cutoff */}
        <div className="text-center mb-16">
          <RevealLine delay={0.1}>
            <h2 className="uppercase text-white leading-none"
              style={{ fontFamily: BF, fontSize: 'clamp(52px, 8vw, 120px)', letterSpacing: '0.01em' }}>HABLEMOS</h2>
          </RevealLine>
          <RevealLine delay={0.22}>
            <h2 className="uppercase leading-none"
              style={{ fontFamily: BF, fontSize: 'clamp(52px, 8vw, 120px)', letterSpacing: '0.01em', color: '#F5A623' }}>DE TU</h2>
          </RevealLine>
          <RevealLine delay={0.34}>
            <h2 className="uppercase text-white leading-none"
              style={{ fontFamily: BF, fontSize: 'clamp(52px, 8vw, 120px)', letterSpacing: '0.01em' }}>PROYECTO.</h2>
          </RevealLine>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
          <FadeIn delay={0.4}>
            <p className="text-white/55 leading-relaxed mb-10" style={{ fontFamily: SG, fontSize: 'clamp(16px, 1.1vw, 19px)', maxWidth: '440px' }}>
              Cuéntame qué negocio tienes y qué necesitas. Te recomendaré la opción que mejor encaje. Respuesta rápida y sin compromiso.
            </p>
            <a href="https://wa.me/34633015650?text=Hola%20Jos%C3%A9%2C%20he%20visto%20tu%20portfolio%20y%20quiero%20informarme."
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-3 text-white/35 hover:text-amber-400 transition-colors duration-300 group"
              style={{ fontFamily: MONO, fontSize: '12px', letterSpacing: '0.2em' }}>
              <span className="uppercase">WhatsApp directo</span>
              <span className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1">↗</span>
            </a>
          </FadeIn>

          <FadeIn delay={0.5}>
            {status === 'success' ? (
              <motion.div data-testid={CONTACTO.successMessage} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }} className="py-12">
                <div className="text-amber-400 uppercase mb-5"
                  style={{ fontFamily: BF, fontSize: 'clamp(32px, 4vw, 60px)', letterSpacing: '0.01em', lineHeight: 1 }}>
                  MENSAJE<br />ENVIADO ↗
                </div>
                <p className="text-white/40" style={{ fontFamily: SG, fontSize: '16px' }}>Te respondo en menos de 24 horas.</p>
              </motion.div>
            ) : (
              <form data-testid={CONTACTO.form} onSubmit={handleSubmit} className="flex flex-col gap-9">
                {[
                  { name: 'name', label: 'Nombre', type: 'text', placeholder: 'Tu nombre', testId: CONTACTO.nameInput },
                  { name: 'email', label: 'Email', type: 'email', placeholder: 'tu@email.com', testId: CONTACTO.emailInput },
                ].map((f) => (
                  <div key={f.name}>
                    <label className="block text-white/35 text-xs tracking-[0.25em] uppercase mb-3" style={{ fontFamily: MONO }}>{f.label}</label>
                    <input type={f.type} name={f.name} value={form[f.name]} onChange={handleChange}
                      placeholder={f.placeholder} required data-testid={f.testId}
                      className="w-full bg-transparent border-b border-neutral-800 focus:border-amber-400 pb-3 text-white placeholder-white/20 focus:outline-none transition-colors duration-300"
                      style={{ fontFamily: SG, fontSize: '17px' }} />
                  </div>
                ))}
                <div>
                  <label className="block text-white/35 text-xs tracking-[0.25em] uppercase mb-3" style={{ fontFamily: MONO }}>Mensaje</label>
                  <textarea name="message" value={form.message} onChange={handleChange}
                    placeholder="Cuéntame sobre tu proyecto..." required rows={5}
                    data-testid={CONTACTO.messageInput}
                    className="w-full bg-transparent border-b border-neutral-800 focus:border-amber-400 pb-3 text-white placeholder-white/20 focus:outline-none transition-colors duration-300 resize-none"
                    style={{ fontFamily: SG, fontSize: '17px' }} />
                </div>

                {status === 'error' && (
                  <motion.p data-testid={CONTACTO.errorMessage} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    className="text-red-400 text-xs tracking-wider -mt-2" style={{ fontFamily: MONO }}>
                    {errorMsg}
                  </motion.p>
                )}

                <motion.button type="submit" disabled={status === 'loading'}
                  data-testid={CONTACTO.submitButton} whileHover={{ x: 4 }} whileTap={{ scale: 0.98 }}
                  className="self-start flex items-center gap-4 py-3 text-amber-400 hover:text-white transition-colors duration-300 disabled:opacity-40 bg-transparent border-0"
                  style={{ fontFamily: MONO, fontSize: '13px', letterSpacing: '0.25em' }}>
                  <span className="uppercase">{status === 'loading' ? 'Enviando...' : 'Enviar mensaje'}</span>
                  <motion.span className="text-xl"
                    animate={status === 'loading' ? { rotate: 360 } : {}}
                    transition={{ duration: 1, repeat: status === 'loading' ? Infinity : 0 }}>↗</motion.span>
                </motion.button>
              </form>
            )}
          </FadeIn>
        </div>
      </div>
    </section>
  )
}
